import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { jobsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { ArrowLeft, MapPin, Briefcase, Calendar, CheckCircle, Upload, FileText, Mail, Phone, User } from 'lucide-react';

const Container = styled.div`
  background: ${p => p.theme.colors.background.primary};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}15 0%, ${p => p.theme.colors.primary}08 100%);
  border-bottom: 1px solid ${p => p.theme.colors.border.light};
  padding: 2rem 1rem;
`;

const HeroInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.text.primary};
  cursor: pointer;
  font-size: ${p => p.theme.typography.fontSize.sm};
  transition: all 0.2s ease;
  width: fit-content;
  margin-bottom: 0.5rem;

  &:hover {
    background: ${p => p.theme.colors.background.secondary};
    border-color: ${p => p.theme.colors.primary};
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: ${p => p.theme.typography.fontWeight.bold};
  line-height: 1.2;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-top: 0.5rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  border-radius: ${p => p.theme.borderRadius.full};
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  background: ${p => {
    if (p.variant === 'active') return '#10B98120';
    if (p.variant === 'location') return p.theme.colors.background.secondary;
    return p.theme.colors.background.secondary;
  }};
  color: ${p => {
    if (p.variant === 'active') return '#065F46';
    return p.theme.colors.text.primary;
  }};
`;

const Content = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.border.light};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 2rem;
  box-shadow: ${p => p.theme.shadows.sm};
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem 0;
  color: ${p => p.theme.colors.text.primary};
  font-size: ${p => p.theme.typography.fontSize.xl};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${p => p.theme.colors.border.light};
`;

const Description = styled.div`
  color: ${p => p.theme.colors.text.primary};
  line-height: 1.8;
  font-size: ${p => p.theme.typography.fontSize.base};

  p {
    margin: 0 0 1rem 0;
  }

  ul, ol {
    margin: 0 0 1rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  color: ${p => p.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .required {
    color: ${p => p.theme.colors.error};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.base};
  font-family: ${p => p.theme.typography.fontFamily.primary};
  outline: none;
  transition: all 0.2s ease;
  background: ${p => p.theme.colors.white};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.muted};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 1px solid ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.base};
  font-family: ${p => p.theme.typography.fontFamily.primary};
  outline: none;
  transition: all 0.2s ease;
  min-height: 140px;
  resize: vertical;
  background: ${p => p.theme.colors.white};

  &:focus {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }

  &::placeholder {
    color: ${p => p.theme.colors.text.muted};
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px dashed ${p => p.theme.colors.border.medium};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};

  &:hover {
    border-color: ${p => p.theme.colors.primary};
    background: ${p => p.theme.colors.primary}08;
  }

  ${FileInput}:focus + & {
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}15;
  }
`;

const FileName = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${p => p.theme.colors.primary}15;
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.primary};
  font-size: ${p => p.theme.typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: ${p => p.theme.typography.fontWeight.medium};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${p => p.theme.colors.primary};
  color: ${p => p.theme.colors.white};
  border: none;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.base};
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: ${p => p.theme.shadows.md};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 1rem;
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.typography.fontSize.sm};
  font-weight: ${p => p.theme.typography.fontWeight.medium};
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${p => p.success ? '#10B98120' : '#EF444420'};
  color: ${p => p.success ? '#065F46' : '#991B1B'};
`;

const InfoBox = styled.div`
  padding: 1.25rem;
  background: ${p => p.theme.colors.background.secondary};
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 4px solid ${p => p.theme.colors.primary};
`;

const InfoTitle = styled.div`
  font-weight: ${p => p.theme.typography.fontWeight.semibold};
  color: ${p => p.theme.colors.text.primary};
  margin-bottom: 0.5rem;
  font-size: ${p => p.theme.typography.fontSize.sm};
`;

const InfoText = styled.div`
  font-size: ${p => p.theme.typography.fontSize.sm};
  color: ${p => p.theme.colors.text.secondary};
  line-height: 1.6;
`;

const PublicJobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    cv: null,
    cover_letter: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const res = await jobsAPI.getById(id);
        setJob(res.data);
      } catch (error) {
        console.error('Failed to load job:', error);
        setMessage({ type: 'error', text: 'Failed to load job details.' });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const onChange = e => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const onFile = e => {
    const file = e.target.files?.[0] || null;
    setForm(prev => ({ ...prev, cv: file }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    if (!form.full_name || !form.email) {
      setMessage({ type: 'error', text: 'Please fill in all required fields.' });
      setSubmitting(false);
      return;
    }

    if (!form.cv) {
      setMessage({ type: 'error', text: 'Please upload your CV or resume.' });
      setSubmitting(false);
      return;
    }

    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name);
      fd.append('email', form.email);
      if (form.phone) fd.append('phone', form.phone);
      if (form.cover_letter) fd.append('cover_letter', form.cover_letter);
      fd.append('cv', form.cv);

      await jobsAPI.apply(id, fd);
      setMessage({ type: 'success', text: 'Application submitted successfully! We will review your application and get back to you soon.' });
      setForm({ full_name: '', email: '', phone: '', cv: null, cover_letter: '' });

      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    } catch (e) {
      console.error('Application submission error:', e);
      let errorMessage = 'Failed to submit application. Please try again.';

      if (e.response?.data) {
        if (e.response.data.errors) {
          const errors = e.response.data.errors;
          const errorMessages = Object.entries(errors).map(([field, messages]) => {
            return Array.isArray(messages) ? messages.join(', ') : messages;
          }).flat();
          errorMessage = errorMessages.join('. ') || errorMessage;
        } else if (e.response.data.message) {
          errorMessage = e.response.data.message;
        }
      }

      setMessage({ type: 'error', text: errorMessage });
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Container>
        <PublicHeader theme={theme} />
        <div style={{ padding: '4rem', textAlign: 'center', color: theme.colors.text.secondary }}>
          Loading job details...
        </div>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  if (!job) {
    return (
      <Container>
        <PublicHeader theme={theme} />
        <div style={{ padding: '4rem', textAlign: 'center' }}>
          <h2 style={{ color: theme.colors.text.primary }}>Job not found</h2>
          <p style={{ color: theme.colors.text.secondary }}>The job you're looking for doesn't exist or has been removed.</p>
        </div>
        <PublicFooter theme={theme} />
      </Container>
    );
  }

  return (
    <Container>
      <PublicHeader theme={theme} />
      <Hero theme={theme}>
        <HeroInner>
          <BackButton theme={theme} onClick={() => navigate('/jobs')}>
            <ArrowLeft size={16} />
            Back to Jobs
          </BackButton>
          <Title theme={theme}>{job.title}</Title>
          <MetaRow>
            {job.is_active && (
              <Badge variant="active" theme={theme}>
                <CheckCircle size={14} />
                Open Position
              </Badge>
            )}
            {job.location && (
              <Badge variant="location" theme={theme}>
                <MapPin size={14} />
                {job.location}
              </Badge>
            )}
            {job.employment_type && (
              <Badge theme={theme}>
                <Briefcase size={14} />
                {job.employment_type}
              </Badge>
            )}
            {job.deadline && (
              <Badge theme={theme}>
                <Calendar size={14} />
                Apply by {new Date(job.deadline).toLocaleDateString()}
              </Badge>
            )}
          </MetaRow>
        </HeroInner>
      </Hero>

      <Content>
        <MainContent>
          <Card theme={theme}>
            <SectionTitle theme={theme}>Job Description</SectionTitle>
            <Description theme={theme} dangerouslySetInnerHTML={{ __html: job.description || 'No description provided.' }} />
          </Card>

          {job.requirements && (
            <Card theme={theme}>
              <SectionTitle theme={theme}>Requirements</SectionTitle>
              <Description theme={theme} dangerouslySetInnerHTML={{ __html: job.requirements }} />
            </Card>
          )}
        </MainContent>

        <Sidebar>
          <Card theme={theme}>
            <SectionTitle theme={theme} style={{ fontSize: theme.typography.fontSize.lg }}>
              Apply for this Position
            </SectionTitle>
            <InfoBox theme={theme}>
              <InfoTitle theme={theme}>Application Tips</InfoTitle>
              <InfoText theme={theme}>
                Make sure your CV is up to date and your cover letter highlights why you're a great fit for this role.
              </InfoText>
            </InfoBox>

            <form onSubmit={submit} style={{ marginTop: '1.5rem' }}>
              <FormGroup>
                <Label theme={theme}>
                  <User size={16} />
                  Full Name
                  <span className="required">*</span>
                </Label>
                <Input
                  required
                  name="full_name"
                  value={form.full_name}
                  onChange={onChange}
                  placeholder="Enter your full name"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>
                  <Mail size={16} />
                  Email Address
                  <span className="required">*</span>
                </Label>
                <Input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="your.email@example.com"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>
                  <Phone size={16} />
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+251 9XX XXX XXX"
                  theme={theme}
                />
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>
                  <Upload size={16} />
                  CV / Resume
                  <span className="required">*</span>
                </Label>
                <FileInputWrapper>
                  <FileInput
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={onFile}
                    id="cv-upload"
                    required
                  />
                  <FileInputLabel htmlFor="cv-upload" theme={theme}>
                    <Upload size={20} />
                    <span>{form.cv ? form.cv.name : 'Choose file (PDF, DOC, DOCX)'}</span>
                  </FileInputLabel>
                </FileInputWrapper>
                {form.cv && (
                  <FileName theme={theme}>
                    <FileText size={16} />
                    {form.cv.name}
                  </FileName>
                )}
              </FormGroup>

              <FormGroup>
                <Label theme={theme}>
                  <FileText size={16} />
                  Cover Letter (Optional)
                </Label>
                <Textarea
                  name="cover_letter"
                  value={form.cover_letter}
                  onChange={onChange}
                  placeholder="Tell us why you're interested in this position and what makes you a great fit..."
                  theme={theme}
                />
              </FormGroup>

              <SubmitButton
                type="submit"
                disabled={submitting}
                theme={theme}
              >
                {submitting ? (
                  <>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    <span>Submit Application</span>
                  </>
                )}
              </SubmitButton>

              {message.text && (
                <Message success={message.type === 'success'} theme={theme}>
                  {message.type === 'success' ? <CheckCircle size={18} /> : <span>⚠️</span>}
                  {message.text}
                </Message>
              )}
            </form>
          </Card>
        </Sidebar>
      </Content>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default PublicJobDetail;
